import React from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import Sidebar from '../components/sidebar/Sidebar';
import Header from '../components/header/Header';
import { useUIStore } from '../stores/uiStore';
import { NotificationContainer } from '../components/ui/Notification';
import { IMainLayoutProps } from '../types/interfaces/ILayout';

const MainLayout: React.FC<IMainLayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { sidebarOpen, toggleSidebar, notifications, removeNotification } =
    useUIStore();

  const sidebarVariants = {
    open: {
      x: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 25,
        duration: 0.3,
      },
    },
    closed: {
      x: '-100%',
      opacity: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 25,
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    open: {
      width: isMobile ? '100vw' : 'calc(100vw - 280px)',
      marginLeft: isMobile ? 0 : 280,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 25,
        duration: 0.3,
      },
    },
    closed: {
      width: '100vw',
      marginLeft: 0,
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 25,
        duration: 0.3,
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        width: '100vw',
        bgcolor: 'background.default',
        overflow: 'hidden',
        position: 'relative',
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {/* Overlay for mobile */}
      {isMobile && sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1200,
          }}
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <motion.div
        variants={sidebarVariants}
        animate={sidebarOpen ? 'open' : 'closed'}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          zIndex: 1300,
          height: '100vh',
          width: 280,
        }}
      >
        <Sidebar />
      </motion.div>

      {/* Main Content */}
      <motion.div
        variants={contentVariants}
        animate={sidebarOpen ? 'open' : 'closed'}
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
        }}
      >
        <Header />
        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
            bgcolor: 'background.default',
            width: '100%',
            height: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </Box>
      </motion.div>

      {/* Notifications */}
      <NotificationContainer
        notifications={notifications}
        onClose={removeNotification}
      />
    </Box>
  );
};

export default MainLayout;
